import { CircleHelp } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '@/utils/cn';

import type { PopoverPortalTarget } from './popover-portal';
import { Tooltip } from './tooltip';

export type TipViewMode = 'inline' | 'icon' | 'label';

export interface TipViewProps {
  mode: TipViewMode;
  content: string;
  /** Accessible name for the icon trigger. */
  moreAboutLabel: string;
  /** For `label` mode — the text that receives the dotted underline. */
  children?: ReactNode;
  popoverPortal?: PopoverPortalTarget;
  className?: string;
  /** Stable id for inline captions (FormField aria-describedby). */
  id?: string;
}

const HOVER_OPEN_MS = 300;

/**
 * Presentational tip chrome. Product tips resolve copy via `Tip` in shared/; this
 * primitive only paints inline / icon / label modes.
 *
 * Icon tips open on pointer hover and keyboard focus (`:focus-visible`), not on
 * programmatic focus — a dialog that autofocuses the first control must not pop
 * the help bubble every time it opens.
 */
export const TipView = ({
  mode,
  content,
  moreAboutLabel,
  children,
  popoverPortal = 'body',
  className,
  id,
}: TipViewProps) => {
  if (mode === 'inline') {
    return (
      <p id={id} className={cn('text-caption text-fg-secondary', className)}>
        {content}
      </p>
    );
  }

  if (mode === 'label') {
    return (
      <Tooltip content={content} popoverPortal={popoverPortal}>
        <span
          className={cn(
            'cursor-help text-body-sm text-fg-secondary underline decoration-dotted underline-offset-2',
            className,
          )}
        >
          {children}
        </span>
      </Tooltip>
    );
  }

  return (
    <IconTip
      content={content}
      moreAboutLabel={moreAboutLabel}
      popoverPortal={popoverPortal}
      className={className}
    />
  );
};

const IconTip = ({
  content,
  moreAboutLabel,
  popoverPortal,
  className,
}: {
  content: string;
  moreAboutLabel: string;
  popoverPortal: PopoverPortalTarget;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const hoverTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (hoverTimer.current != null) window.clearTimeout(hoverTimer.current);
    },
    [],
  );

  const clearHoverTimer = () => {
    if (hoverTimer.current != null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  return (
    <Tooltip
      content={content}
      popoverPortal={popoverPortal}
      open={open}
      // Ignore Radix open-from-focus (dialog autofocus). We open via pointer /
      // :focus-visible below; still accept close from Radix (escape, outside).
      onOpenChange={(next) => {
        if (!next) setOpen(false);
      }}
      delayDuration={0}
    >
      <button
        type="button"
        className={cn(
          'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm text-fg-tertiary',
          'hover:text-fg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
          className,
        )}
        aria-label={moreAboutLabel}
        onPointerEnter={() => {
          clearHoverTimer();
          hoverTimer.current = window.setTimeout(() => setOpen(true), HOVER_OPEN_MS);
        }}
        onPointerLeave={() => {
          clearHoverTimer();
          setOpen(false);
        }}
        onFocus={(event) => {
          if (event.currentTarget.matches(':focus-visible')) setOpen(true);
        }}
        onBlur={() => setOpen(false)}
        onClick={() => {
          // Touch: no hover stay — tap toggles.
          if (typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches) {
            clearHoverTimer();
            setOpen((current) => !current);
          }
        }}
      >
        <CircleHelp aria-hidden className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
};
